import store, {StorePath} from './store.service';
import {isLocalhost, logger, loggerError} from './utils.service';
import {OfferModel} from '../models/offer.model';
import {Lead} from '../models/lead.model';
import {Appointment} from '../models/appointment.model';
import {MOCK_ADDITIONAL_APPOINTMENTS} from '../mock/appointment.mock';
import {ERRORS_TO_LOG_ONLY_ONCE} from '../models/errors.model';

const BASE_URL = isLocalhost() ? 'http://localhost/api/' : 'https://fullpath.com/api/';
const GPT_API_BASE_URL = `${BASE_URL}/lab/auto_gpt/`;


const defaultParams = (): URLSearchParams => {
	const params = new URLSearchParams();
	const storeData = store.getState()?.data as Record<string, any>;
	params.append('client', storeData?.[StorePath.TRACK_PARAMS]?.client);
	params.append('track', storeData?.[StorePath.TRACK_DATA]?.track?.id);
	return params;
};

const markErrorAsLogged  = (errorKey: string): void => {
	if (ERRORS_TO_LOG_ONLY_ONCE.hasOwnProperty(errorKey)) {
		ERRORS_TO_LOG_ONLY_ONCE[errorKey] = true;
	}
};

export const initChat = (): Promise<Response> => {
	const path = 'users/me/chat/init';
	const params = defaultParams();
	return get(path, params);
};

export const sendMessage = (message: string): Promise<Response> => {
	const path = 'users/me/chat/message';
	const params = defaultParams();
	params.append('text', message);
	return get(path, params);
};

export const expressInterestInOffer = (offer: OfferModel): Promise<Response> => {
	const path = `users/me/chat/offers/${offer.id}`;
	const params = defaultParams();
	params.append('offer_type', offer.type);
	return get(path, params);
};

export const expressInterestInCar = (carId: string): Promise<Response> => {
	const path = `users/me/chat/cars/${carId}`;
	const params = defaultParams();
	return get(path, params);
};

export const expressInterestInLocation = (location: string): Promise<Response> => {
	const path = `users/me/chat/location/${encodeURIComponent(location)}`;
	const params = defaultParams();
	return get(path, params);
};

export const leadConversion = (payload: Lead): Promise<Response> => {
	const path = `users/me/chat/conversion`;
	const params = defaultParams();
	params.append('payload', JSON.stringify(payload));
	return get(path, params);
};

export const bookAppointment = (payload: {lead: Lead, appointment: Appointment}): Promise<Response> => {
	const path = `users/me/chat/book_appointment`;
	if (isLocalhost()) {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve({ok: true, json: async () => ({id: '1234', is_test: true})} as Response);
			}, 2000);
		});
	}
	return post(path, payload);
};

export const getAvailableAppointments = (from: string): Promise<Response> => {
	const path = `users/me/chat/available_appointments`;
	if (isLocalhost()) {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve({ok: true, json: async () => ({appointments: MOCK_ADDITIONAL_APPOINTMENTS})} as Response);
			}, 2000);
		});
	}
	const params = defaultParams();
	params.append('from', from);
	return get(path, params);
};

const configureErrorString = (error: Error): string => {
	let errorStr = error.message;
	if (error.stack) {
		errorStr = `${errorStr}\n${error.stack}`;
	} else if (!error.message) {
		errorStr = JSON.stringify(error);
	}
	return errorStr;
};

export const logJsError = (error: Error) => {
	const errorKey = error.message;
	if (ERRORS_TO_LOG_ONLY_ONCE[errorKey]) {
		return;
	}
	loggerError(error);
	const errorStr = configureErrorString(error);
	const path = `jserror`;
	const params = defaultParams();
	const trackParams = store.getState()?.data?.track_params as {[T: string]: string};
	params.append('url', trackParams.url);
	params.append('forty_n_t', trackParams.forty_n_t);
	params.append('browser', trackParams.browser);
	params.append('browser_ver', trackParams.browser_ver);
	params.append('identifier', 'Chat');
	params.append('error', errorStr);
	params.append('slack', '#js-errors-chat');

	get(path, params).then(
		(res) => {
			logger('error successfully logged', res);
		},
		(error) => {
			logger('error', error);
		}
	).finally(() => {
		markErrorAsLogged(errorKey);
    });
};

export const get = async (path: string, params: URLSearchParams, baseUrl = BASE_URL): Promise<Response> => {
	const url = new URL(`${baseUrl}${path}`);
	url.search = params.toString();
	return await fetch(url);
};

export const post = async (path: string, data: Record<string, any>, baseUrl = BASE_URL): Promise<Response> => {
	const params = defaultParams();
	const url = new URL(`${baseUrl}${path}`);
	url.search = params.toString();
	return await fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		body: JSON.stringify(data),
	});
};

export const put = async <T>(path: string, data: object): Promise<T> => {
	const url = new URL(path, GPT_API_BASE_URL);
	const response = await fetch(url, {
		method: "PUT",
		body: JSON.stringify(data),
		headers: {
			"Content-Type": "application/json",
		},
	});
	return response.json() as Promise<T>;
};

export const remove = async <T>(path: string): Promise<T> => {
	const url = new URL(path, GPT_API_BASE_URL);
	const response = await fetch(url, {
		method: "DELETE",
	});
	return response.json() as Promise<T>;
};

export const handleApiResponse = (response: Response, returnResponsePromise = false) => {
	if (response.ok) {
		const contentType = response.headers.get('Content-Type');
		if (contentType?.includes('application/json')) {
			return returnResponsePromise ? response : response.json();
		} else {
			throw new Error('Unexpected response format: ' + contentType);
		}
	} else {
		response.text()
			.then((responseText) => {
				throw new Error(`Network response was not OK: ${responseText}`);
			})
			.catch((error) => {
				if (error?.message) {
					throw error;
				}
				throw new Error(`Network response was not OK. No Text`);
			});
	}
};
